import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import {
  ArrowUp,
  BookOpen,
  Box,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  CirclePlus,
  Copy,
  Filter,
  Folder,
  Globe2,
  Home,
  Image,
  KeyRound,
  Layers3,
  Lightbulb,
  LockKeyhole,
  LogOut,
  Mail,
  Menu,
  MessageSquare,
  MoreHorizontal,
  Palette,
  Pencil,
  PlaySquare,
  Plus,
  Settings2,
  Share2,
  Sparkles,
  Star,
  Trash2,
  Upload,
  UserRound,
  Video,
  WandSparkles,
  Zap,
  type LucideIcon,
} from 'lucide-react';

type CanvasKind = 'classic' | 'smart';

type CanvasRecord = {
  id: string;
  title: string;
  icon?: string;
  kind?: CanvasKind;
  owner?: string;
  color?: string;
  pinned?: boolean;
  created_at?: number;
  updated_at?: number;
  deleted_at?: number;
  node_count?: number;
  public_project_code?: string;
  readonly?: boolean;
};

type CreateCanvasOptions = {
  title?: string;
  icon?: string;
  kind?: CanvasKind;
};

type CreateCanvas = (options?: CreateCanvasOptions) => void;
type RenameCanvas = (canvas: CanvasRecord, title: string) => Promise<boolean>;
type DeleteCanvas = (canvas: CanvasRecord) => Promise<boolean>;
type ShareCanvas = (canvas: CanvasRecord) => Promise<void>;
type OpenProjectCode = (code: string) => Promise<void>;

type Route =
  | { name: 'home' }
  | { name: 'projects' }
  | { name: 'templates' }
  | { name: 'original' }
  | { name: 'canvas'; canvasId: string };

type NavItem = {
  key: Exclude<Route['name'], 'canvas'>;
  label: string;
  path: string;
  icon: LucideIcon;
};

type InspirationItem = {
  id: string;
  title: string;
  author: string;
  image?: string;
  height: number;
  tone: string;
  views: number;
  likes: number;
  category: string;
};

type VoxelSkill = {
  id: string;
  title: string;
  category: string;
  description: string;
  source_url?: string;
  local_path?: string;
  license?: string;
  installed?: boolean;
  prompt?: string;
};

type SkillRegistry = {
  version?: number;
  categories?: string[];
  skills?: VoxelSkill[];
};

type AuthUser = {
  id: string;
  email: string;
  display_name: string;
  role?: string;
  credits?: number;
  created_at?: string;
};

type AuthState = {
  authenticated: boolean;
  user: AuthUser | null;
  credits: { balance: number };
  loading: boolean;
};

type AuthResponse = {
  authenticated: boolean;
  user: AuthUser | null;
  credits?: { balance: number };
};

type RegisterCodeResponse = {
  ok: boolean;
  sent: boolean;
  debug_code?: string;
};

type RedeemKeyResponse = {
  ok: boolean;
  message: string;
};

type AccountProps = {
  auth: AuthState;
  onLogout: () => void;
};

const navItems: NavItem[] = [
  { key: 'home', label: '首页', path: '/app/', icon: Home },
  { key: 'projects', label: '项目', path: '/app/projects', icon: Folder },
  { key: 'templates', label: '灵感发现', path: '/app/templates', icon: Palette },
  { key: 'original', label: '原始参考', path: '/app/original', icon: Layers3 },
];

const templateCategories = ['全部', '品牌设计', '海报与广告', '插画', 'UI设计', '角色设计', '影片与分镜', '产品设计', '建筑设计'];

const fallbackSkillCategories = ['Video', 'Social Media', 'E-Commerce', 'Branding', 'Marketing', 'Studio'];

const VOXEL_LOGO_SRC = '/static/images/voxel-logo-master.svg';
const VOXEL_ROUNDED_LOGO_SRC = '/static/images/voxel-logo-rounded-app.svg';
const VOXEL_LOADER_SRC = '/static/images/voxel-logo-loader-animated.svg';
const CANVAS_CREATE_MIN_LOADER_MS = 850;

const skillCategoryLabels: Record<string, string> = {
  Video: '视频',
  'Social Media': '社交媒体',
  'E-Commerce': '电商',
  Branding: '品牌',
  Marketing: '营销',
  Studio: '工作室',
};

const skillCopyLabels: Record<string, { title: string; description: string; prompt?: string }> = {
  'video-use': {
    title: '视频剪辑',
    description: '把原始素材剪成完整成片，支持转录剪辑、字幕、调色、叠加元素和自评检查。',
    prompt: '使用视频剪辑技能，把上传素材整理成一条完成度高的发布视频。',
  },
  'hyperframes-video': {
    title: '连续帧动画',
    description: '生成可用于 Hyperframes 或 Remotion 渲染的连续帧动画脚本。',
    prompt: '使用连续帧动画技能，把我的想法转成有镜头感的逐帧视频脚本。',
  },
  'manim-video': {
    title: 'Manim 技术动画',
    description: '用 Manim 制作技术讲解、算法可视化和建筑/空间动画。',
    prompt: '使用 Manim 技术动画技能，为这个概念制作一段动画讲解。',
  },
  'comfyui-workflow': {
    title: 'ComfyUI 工作流',
    description: '生成可导入 ComfyUI 的图像、视频、放大和模型管线节点工作流。',
    prompt: '使用 ComfyUI 工作流技能，为这个生成任务创建节点流程。',
  },
  'xiaohongshu-card': {
    title: '小红书卡片',
    description: '把内容整理成可滑动的小红书风格知识卡片组。',
    prompt: '使用小红书卡片技能，把我的内容转成可滑动的社交媒体卡片。',
  },
  'poster-hero': {
    title: '营销海报',
    description: '创建竖版发布海报、朋友圈分享图和强视觉冲击的推广视觉。',
    prompt: '使用营销海报技能，创建一张竖版社交发布海报。',
  },
  'ecommerce-image-workflow': {
    title: '电商图片工作流',
    description: '基于产品参考图生成主图、卖点图和生活方式场景图。',
    prompt: '使用电商图片工作流技能，基于我的参考图制作一组产品图。',
  },
  brandkit: {
    title: '品牌套件',
    description: '创建包含 Logo 方向、色彩、字体、应用场景和视觉世界观的品牌板。',
    prompt: '使用品牌套件技能，为这个产品创建一套高级品牌系统板。',
  },
  'brand-guidelines': {
    title: '品牌规范',
    description: '用结构化色彩、字体和视觉规则保持所有产出风格一致。',
    prompt: '使用品牌规范技能，为这个项目建立一致的视觉身份。',
  },
  'ad-creative': {
    title: '广告创意',
    description: '生成并迭代付费社交和搜索广告创意，包括钩子、标题、正文和变体。',
    prompt: '使用广告创意技能，生成付费社媒广告概念和文案变体。',
  },
  'marketing-psychology': {
    title: '营销心理学',
    description: '用行为科学优化钩子、叙事框架、价格呈现和说服性文案。',
    prompt: '使用营销心理学技能，强化定位和转化表达。',
  },
  'creative-director': {
    title: '创意总监',
    description: '从 brief 到风格方向、评审、资源匹配和最终润色，统筹完整设计流程。',
    prompt: '使用创意总监技能，规划这个设计工作流并评审方向。',
  },
  'design-review': {
    title: '设计评审',
    description: '对已完成 UI 做视觉审查和精修建议，给出前后对比思路。',
    prompt: '使用设计评审技能，审查这个 UI 并提出精修方案。',
  },
  'canvas-design': {
    title: '画布视觉设计',
    description: '基于设计理念和美学原则，创建海报、插画和静态视觉作品。',
    prompt: '使用画布视觉设计技能，在无限画布上创建一张完成度高的视觉作品。',
  },
};

const fallbackSkills: VoxelSkill[] = [
  {
    id: 'video-use',
    title: 'Video Use',
    category: 'Video',
    description: 'Edit raw footage into polished videos with transcript-driven cuts, subtitles, grading, overlays, and self-evaluation.',
    license: 'MIT',
    installed: true,
  },
  {
    id: 'hyperframes-video',
    title: 'Hyperframes Video',
    category: 'Video',
    description: 'Generate continuous frame animation scripts that can be rendered by Hyperframes or Remotion.',
    license: 'Apache-2.0',
    installed: true,
  },
  {
    id: 'comfyui-workflow',
    title: 'ComfyUI Workflow',
    category: 'Video',
    description: 'Generate importable ComfyUI workflows for image, video, upscaling, and model pipelines.',
    license: 'MIT',
    installed: true,
  },
  {
    id: 'xiaohongshu-card',
    title: 'Xiaohongshu Card',
    category: 'Social Media',
    description: 'Turn content into a swipeable Xiaohongshu-style knowledge-card carousel.',
    license: 'Apache-2.0',
    installed: true,
  },
  {
    id: 'ecommerce-image-workflow',
    title: 'Ecommerce Image Workflow',
    category: 'E-Commerce',
    description: 'Generate product-faithful main, feature, and lifestyle images from uploaded product references.',
    license: 'Apache-2.0',
    installed: true,
  },
  {
    id: 'brandkit',
    title: 'Brandkit',
    category: 'Branding',
    description: 'Create a premium brand-kit board with logo direction, palette, typography, applications, and visual world.',
    license: 'Apache-2.0',
    installed: true,
  },
  {
    id: 'ad-creative',
    title: 'Ad Creative',
    category: 'Marketing',
    description: 'Generate and iterate paid social and search ad creative: hooks, headlines, primary text, and variants.',
    license: 'Apache-2.0',
    installed: true,
  },
  {
    id: 'creative-director',
    title: 'Creative Director',
    category: 'Studio',
    description: 'Direct a full design flow from brief to style direction, critique, resource matching, and polish.',
    license: 'Apache-2.0',
    installed: true,
  },
];

const inspirationItems: InspirationItem[] = [
  {
    id: 'akari',
    title: '柔和产品视觉',
    author: 'Sophie_Bakes12',
    image: '/static/runninghub/thumbnails/workflow-2058554058318897153.jpg',
    height: 500,
    tone: '#b7865b',
    views: 5525,
    likes: 200,
    category: '产品设计',
  },
  {
    id: 'hotel',
    title: '建筑概念展示',
    author: 'clvisual',
    image: '/static/runninghub/thumbnails/workflow-2058541134623891458.jpg',
    height: 306,
    tone: '#d8a398',
    views: 5451,
    likes: 173,
    category: '建筑设计',
  },
  {
    id: 'lookbook',
    title: '社媒穿搭套图',
    author: 'Sophie_Bakes12',
    image: '/static/runninghub/thumbnails/workflow-2058824859437850625.jpg',
    height: 274,
    tone: '#d4c9be',
    views: 5686,
    likes: 217,
    category: '海报与广告',
  },
  {
    id: 'beats',
    title: '怀旧包装视觉',
    author: 'Oliver_Smith',
    height: 274,
    tone: '#c79a72',
    views: 3205,
    likes: 181,
    category: '品牌设计',
  },
  {
    id: 'osmo',
    title: '科技产品广告',
    author: 'RainySunday22',
    image: '/static/runninghub/thumbnails/workflow-2058818588181622785.jpg',
    height: 544,
    tone: '#111827',
    views: 11332,
    likes: 300,
    category: '产品设计',
  },
  {
    id: 'magsafe',
    title: '手机配件广告',
    author: 'Ava_Studio',
    height: 320,
    tone: '#dfe8f6',
    views: 4320,
    likes: 154,
    category: '海报与广告',
  },
  {
    id: 'packaging',
    title: '香氛包装提案',
    author: 'Emma_Wilson',
    height: 386,
    tone: '#c3b7a7',
    views: 5217,
    likes: 141,
    category: '品牌设计',
  },
  {
    id: 'character',
    title: '角色海报',
    author: 'Emma_Wilson',
    height: 266,
    tone: '#416f9e',
    views: 5217,
    likes: 141,
    category: '角色设计',
  },
  {
    id: 'sneaker',
    title: '球鞋细节页',
    author: 'Luna_Design',
    height: 310,
    tone: '#b8453f',
    views: 2812,
    likes: 96,
    category: '产品设计',
  },
  {
    id: 'story',
    title: '品牌故事版式',
    author: 'Mira_Lab',
    height: 248,
    tone: '#e5d2bd',
    views: 3108,
    likes: 111,
    category: 'UI设计',
  },
];

function parseRoute(): Route {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/app';
  const canvasMatch = pathname.match(/^\/app\/canvas\/([^/]+)$/);
  if (canvasMatch?.[1]) return { name: 'canvas', canvasId: decodeURIComponent(canvasMatch[1]) };
  if (pathname === '/app/projects') return { name: 'projects' };
  if (pathname === '/app/templates') return { name: 'templates' };
  if (pathname === '/app/original') return { name: 'original' };
  return { name: 'home' };
}

function routePath(route: Route): string {
  if (route.name === 'projects') return '/app/projects';
  if (route.name === 'templates') return '/app/templates';
  if (route.name === 'original') return '/app/original';
  if (route.name === 'canvas') return `/app/canvas/${encodeURIComponent(route.canvasId)}`;
  return '/app/';
}

function navigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function formatDate(value?: number) {
  if (!value) return '2026年6月5日';
  return new Intl.DateTimeFormat('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(value));
}

function formatCredits(value?: number) {
  return new Intl.NumberFormat('zh-CN').format(Math.max(0, Number(value || 0)));
}

function normalizeProjectCode(value: string) {
  return value.trim().toUpperCase().replace(/\s+/g, '');
}

function isPublicProjectCode(value: string) {
  return /^VX-[A-Z2-9]{8,}$/.test(normalizeProjectCode(value));
}

async function copyText(value: string) {
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.style.position = 'fixed';
    textarea.style.left = '-999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    textarea.remove();
    return ok;
  }
}

function apiJson<T>(url: string, init?: RequestInit): Promise<T> {
  return fetch(url, {
    ...init,
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  }).then(async (response) => {
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let message = text || `Request failed: ${response.status}`;
      try {
        const parsed = JSON.parse(text) as { detail?: unknown; message?: string };
        if (typeof parsed.detail === 'string') message = parsed.detail;
        else if (typeof parsed.message === 'string') message = parsed.message;
        else if (parsed.detail) message = JSON.stringify(parsed.detail);
      } catch {
        // Keep raw text.
      }
      const error = new Error(message);
      (error as Error & { status?: number }).status = response.status;
      throw error;
    }
    return response.json() as Promise<T>;
  });
}

function useAuth() {
  const [auth, setAuth] = useState<AuthState>({
    authenticated: false,
    user: null,
    credits: { balance: 0 },
    loading: true,
  });

  const applyAuth = useCallback((data: AuthResponse) => {
    setAuth({
      authenticated: Boolean(data.authenticated && data.user),
      user: data.user,
      credits: data.credits || { balance: data.user?.credits || 0 },
      loading: false,
    });
  }, []);

  const refresh = useCallback(async () => {
    setAuth((current) => ({ ...current, loading: true }));
    try {
      applyAuth(await apiJson<AuthResponse>('/api/platform/me', { cache: 'no-store' }));
    } catch {
      setAuth({ authenticated: false, user: null, credits: { balance: 0 }, loading: false });
    }
  }, [applyAuth]);

  const logout = useCallback(async () => {
    await apiJson<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }).catch(() => ({ ok: true }));
    setAuth({ authenticated: false, user: null, credits: { balance: 0 }, loading: false });
    navigate('/app/');
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { auth, applyAuth, refresh, logout };
}

function useCanvases(enabled: boolean) {
  const [canvases, setCanvases] = useState<CanvasRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    if (!enabled) {
      setCanvases([]);
      setLoading(false);
      setError('');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await apiJson<{ canvases: CanvasRecord[] }>('/api/canvases', { cache: 'no-store' });
      setCanvases(data.canvases || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { canvases, loading, error, refresh, setCanvases };
}

function useSkills() {
  const [registry, setRegistry] = useState<SkillRegistry>({
    categories: fallbackSkillCategories,
    skills: fallbackSkills,
  });

  useEffect(() => {
    let cancelled = false;
    apiJson<SkillRegistry>('/api/platform/skills', { cache: 'no-store' })
      .then((data) => {
        if (cancelled) return;
        setRegistry({
          ...data,
          categories: data.categories?.length ? data.categories : fallbackSkillCategories,
          skills: data.skills?.length ? data.skills : fallbackSkills,
        });
      })
      .catch(() => {
        if (!cancelled) setRegistry({ categories: fallbackSkillCategories, skills: fallbackSkills });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return registry;
}

function skillIcon(skill: VoxelSkill): LucideIcon {
  const id = `${skill.category} ${skill.id}`.toLowerCase();
  if (id.includes('video') || id.includes('hyperframes') || id.includes('manim')) return Video;
  if (id.includes('social') || id.includes('xiaohongshu') || id.includes('poster')) return Image;
  if (id.includes('commerce') || id.includes('product')) return Box;
  if (id.includes('brand') || id.includes('guidelines')) return Palette;
  if (id.includes('marketing') || id.includes('ad-creative')) return Sparkles;
  if (id.includes('director') || id.includes('review') || id.includes('canvas')) return WandSparkles;
  return BookOpen;
}

function localizedSkillCategory(category: string) {
  return skillCategoryLabels[category] || category;
}

function localizedSkill(skill: VoxelSkill) {
  const copy = skillCopyLabels[skill.id];
  return {
    title: copy?.title || skill.title,
    description: copy?.description || skill.description,
    prompt: copy?.prompt || skill.prompt,
  };
}

function LogoMark({ small = false, variant = 'circle' }: { small?: boolean; variant?: 'circle' | 'rounded' }) {
  const src = variant === 'rounded' ? VOXEL_ROUNDED_LOGO_SRC : VOXEL_LOGO_SRC;

  return (
    <span className={`logo-mark ${small ? 'small' : ''} ${variant === 'rounded' ? 'rounded' : ''}`}>
      <img src={src} alt="" aria-hidden="true" draggable={false} />
    </span>
  );
}

function CanvasCreationOverlay() {
  return (
    <div className="canvas-create-overlay" role="status" aria-live="polite" aria-label="正在分配画布 ID">
      <div className="canvas-create-loader">
        <img src={VOXEL_LOADER_SRC} alt="" aria-hidden="true" draggable={false} />
        <span>正在分配画布 ID</span>
      </div>
    </div>
  );
}

function AuthView({ onAuthenticated, nextPath = '/app/' }: { onAuthenticated: (data: AuthResponse) => void; nextPath?: string }) {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [debugCode, setDebugCode] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const isRegister = mode === 'register';

  function resetStatus() {
    setMessage('');
    setDebugCode('');
  }

  async function requestCode() {
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setMessage('请先输入邮箱');
      return;
    }
    setSendingCode(true);
    setMessage('');
    setDebugCode('');
    try {
      const data = await apiJson<RegisterCodeResponse>('/api/auth/register/request-code', {
        method: 'POST',
        body: JSON.stringify({ email: cleanEmail }),
      });
      setMessage(data.sent ? '验证码已发送到邮箱' : '开发模式验证码已生成');
      if (data.debug_code) {
        setDebugCode(data.debug_code);
        setCode(data.debug_code);
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '验证码发送失败');
    } finally {
      setSendingCode(false);
    }
  }

  async function submitAuth(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setMessage('');
    try {
      const endpoint = isRegister ? '/api/auth/register' : '/api/auth/login';
      const body = isRegister
        ? { email: email.trim(), display_name: displayName.trim(), password, code: code.trim() }
        : { email: email.trim(), password };
      const data = await apiJson<AuthResponse>(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      onAuthenticated(data);
      navigate(nextPath);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '认证失败');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-panel">
        <div className="auth-brand">
          <LogoMark variant="rounded" />
          <div>
            <strong>Voxel</strong>
            <span>无限画布平台</span>
          </div>
        </div>
        <div className="auth-copy">
          <h1>{isRegister ? '创建 Voxel 账户' : '登录 Voxel'}</h1>
          <p>用邮箱管理你的无限画布项目、分享编号和未来积分。</p>
        </div>
        <div className="auth-tabs" role="tablist" aria-label="账户入口">
          <button
            className={!isRegister ? 'active' : ''}
            type="button"
            role="tab"
            aria-selected={!isRegister}
            onClick={() => {
              setMode('login');
              resetStatus();
            }}
          >
            登录
          </button>
          <button
            className={isRegister ? 'active' : ''}
            type="button"
            role="tab"
            aria-selected={isRegister}
            onClick={() => {
              setMode('register');
              resetStatus();
            }}
          >
            注册
          </button>
        </div>
        <form className="auth-form" onSubmit={submitAuth}>
          <label>
            <span>邮箱</span>
            <div className="auth-input-wrap">
              <Mail size={16} />
              <input
                value={email}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
          </label>
          {isRegister && (
            <label>
              <span>昵称</span>
              <div className="auth-input-wrap">
                <UserRound size={16} />
                <input
                  value={displayName}
                  autoComplete="name"
                  placeholder="可选"
                  onChange={(event) => setDisplayName(event.target.value)}
                />
              </div>
            </label>
          )}
          <label>
            <span>密码</span>
            <div className="auth-input-wrap">
              <LockKeyhole size={16} />
              <input
                value={password}
                type="password"
                autoComplete={isRegister ? 'new-password' : 'current-password'}
                placeholder="至少 8 位"
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
              />
            </div>
          </label>
          {isRegister && (
            <label>
              <span>验证码</span>
              <div className="auth-code-row">
                <div className="auth-input-wrap">
                  <KeyRound size={16} />
                  <input
                    value={code}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="邮箱验证码"
                    onChange={(event) => setCode(event.target.value)}
                    required
                  />
                </div>
                <button type="button" onClick={requestCode} disabled={sendingCode}>
                  {sendingCode ? '发送中' : '发送验证码'}
                </button>
              </div>
            </label>
          )}
          {debugCode && <div className="auth-debug-code">开发验证码：{debugCode}</div>}
          {message && <div className="auth-message">{message}</div>}
          <button className="auth-submit" type="submit" disabled={submitting}>
            {submitting ? '处理中' : isRegister ? '注册并进入' : '登录'}
          </button>
        </form>
      </section>
      <section className="auth-showcase" aria-hidden="true">
        <div className="auth-canvas-preview">
          <span />
          <span />
          <span />
          <i />
        </div>
      </section>
    </main>
  );
}

function AccountMenu({ auth, onLogout }: AccountProps) {
  const [open, setOpen] = useState(false);
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);
  const [redeemKey, setRedeemKey] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const user = auth.user;
  const credits = auth.credits.balance;

  useEffect(() => {
    function close(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    }
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, []);

  async function redeemCredits(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRedeeming(true);
    setRedeemMessage('');
    try {
      const data = await apiJson<RedeemKeyResponse>('/api/platform/credits/redeem-key', {
        method: 'POST',
        body: JSON.stringify({ key: redeemKey.trim() }),
      });
      setRedeemMessage(data.message || '积分兑换入口已预留，当前版本暂未启用。');
      setRedeemKey('');
    } catch (err) {
      setRedeemMessage(err instanceof Error ? err.message : '兑换失败');
    } finally {
      setRedeeming(false);
    }
  }

  return (
    <div ref={menuRef} className="account-menu">
      <button className="account-avatar-trigger" type="button" aria-label="账户管理" onClick={() => setOpen((value) => !value)}>
        <LogoMark small variant="rounded" />
      </button>
      {open && (
        <div className="account-popover">
          <div className="account-profile">
            <div className="account-blue-avatar">
              <LogoMark small variant="rounded" />
            </div>
            <div>
              <strong>{user?.display_name || 'Voxel 用户'}</strong>
              <span>{user?.email || '未登录'}</span>
            </div>
          </div>
          <div className="account-plan-row">
            <span>Free</span>
            <strong>
              <Zap size={12} /> {formatCredits(credits)} <ChevronRight size={14} />
            </strong>
          </div>
          <button className="account-upgrade" type="button" onClick={() => setAccountPanelOpen((value) => !value)}>
            账户管理
          </button>
          {accountPanelOpen && (
            <div className="account-manage-panel">
              <div className="account-field-row">
                <span>邮箱</span>
                <strong>{user?.email || '-'}</strong>
              </div>
              <div className="account-field-row">
                <span>积分</span>
                <strong>{formatCredits(credits)}</strong>
              </div>
              <form className="redeem-form" onSubmit={redeemCredits}>
                <label htmlFor="redeem-key">兑换 Key</label>
                <div>
                  <input
                    id="redeem-key"
                    value={redeemKey}
                    onChange={(event) => setRedeemKey(event.target.value)}
                    placeholder="输入兑换 Key"
                    autoComplete="off"
                  />
                  <button type="submit" disabled={redeeming || !redeemKey.trim()}>
                    {redeeming ? '处理中' : '兑换'}
                  </button>
                </div>
                <p>{redeemMessage || '积分兑换入口已预留，当前版本暂未启用。'}</p>
              </form>
            </div>
          )}
          <div className="account-menu-list">
            <button type="button" onClick={() => setAccountPanelOpen((value) => !value)}><UserRound size={15} /> 账户管理</button>
            <button type="button"><BookOpen size={15} /> 使用指南</button>
            <button type="button"><MessageSquare size={15} /> 联系我们</button>
            <button type="button"><Globe2 size={15} /> 简体中文 <ChevronRight size={14} /></button>
            <button type="button"><Sparkles size={15} /> Voxel 技能</button>
          </div>
          <div className="account-divider" />
          <button className="account-logout" type="button" onClick={onLogout}><LogOut size={15} /> 退出登录</button>
        </div>
      )}
    </div>
  );
}

function CreditsBar({ auth, onLogout }: AccountProps) {
  return (
    <div className="credits-bar">
      <div className="credit-pill">
        <Zap size={12} />
        <span>{formatCredits(auth.credits.balance)}</span>
        <button type="button">积分</button>
      </div>
      <AccountMenu auth={auth} onLogout={onLogout} />
    </div>
  );
}

function SideRail({ route, onCreate, creating }: { route: Route; onCreate: CreateCanvas; creating: boolean }) {
  return (
    <aside className="side-rail">
      <div className="rail-main">
        <button className="rail-logo" type="button" aria-label="Voxel" onClick={() => navigate('/app/')}>
          <LogoMark />
        </button>
        <button className="rail-button rail-new" type="button" aria-label="新建项目" onClick={() => onCreate()} disabled={creating}>
          <CirclePlus size={19} />
          <span>{creating ? '创建中' : '新建项目'}</span>
        </button>
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              className={`rail-button ${route.name === item.key ? 'active' : ''}`}
              type="button"
              aria-label={item.label}
              onClick={() => navigate(item.path)}
            >
              <Icon size={19} />
              <span>{item.label}</span>
            </button>
          );
        })}
        <button className="rail-button" type="button" aria-label="账户">
          <UserRound size={19} />
          <span>账户</span>
        </button>
      </div>
      <button className="rail-button rail-help" type="button" aria-label="帮助">
        <CircleHelp size={18} />
      </button>
    </aside>
  );
}

function CanvasPreview({ canvas, index = 0, ghost = false }: { canvas?: CanvasRecord; index?: number; ghost?: boolean }) {
  const palette = ['#f7f7f7', '#f3f0ec', '#eef2f6', '#f7ece8', '#edf4ef'];
  const color = ghost ? '#f4f4f4' : palette[index % palette.length];
  const line = ['#8067ff', '#111827', '#b08968', '#0f766e', '#be123c'][index % 5];
  return (
    <div className={`canvas-preview ${ghost ? 'ghost' : ''}`} style={{ '--preview-bg': color, '--preview-line': line } as CSSProperties}>
      {!ghost && (
        <>
          <span className="preview-card a" />
          <span className="preview-card b" />
          <span className="preview-card c" />
          <span className="preview-line one" />
          <span className="preview-line two" />
        </>
      )}
      {ghost && <Plus size={24} />}
      {canvas?.pinned && <span className="pin-dot" />}
    </div>
  );
}

function ProjectTile({
  canvas,
  index,
  onOpen,
  onRename,
  onDelete,
  onShare,
  placeholder = false,
  newTile = false,
  onCreate,
}: {
  canvas?: CanvasRecord;
  index: number;
  onOpen?: (canvas: CanvasRecord) => void;
  onRename?: RenameCanvas;
  onDelete?: (canvas: CanvasRecord) => void;
  onShare?: ShareCanvas;
  placeholder?: boolean;
  newTile?: boolean;
  onCreate?: CreateCanvas;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const title = newTile ? '新建项目' : canvas?.title || (placeholder ? 'Untitled' : '未命名');
  const date = newTile ? '' : `更新于 ${formatDate(canvas?.updated_at || canvas?.created_at)}`;
  const canManage = Boolean(canvas && !placeholder && !newTile);
  const canMutate = Boolean(canManage && !canvas?.readonly);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function close(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [menuOpen]);

  useEffect(() => {
    if (!editing) return;
    const input = titleInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [editing]);

  function startRename() {
    if (!canvas) return;
    setDraftTitle(canvas.title || 'Untitled');
    setEditing(true);
  }

  async function commitRename() {
    if (!canvas || savingTitle) return;
    const nextTitle = draftTitle.trim();
    const currentTitle = canvas.title || 'Untitled';
    if (!nextTitle) {
      setDraftTitle(currentTitle);
      setEditing(false);
      return;
    }
    if (nextTitle === currentTitle) {
      setEditing(false);
      return;
    }

    setSavingTitle(true);
    try {
      const ok = await onRename?.(canvas, nextTitle);
      if (ok !== false) setEditing(false);
    } finally {
      setSavingTitle(false);
    }
  }

  return (
    <article className="project-tile">
      <button
        className="project-thumb-button"
        type="button"
        onClick={() => {
          if (newTile) onCreate?.();
          else if (canvas) onOpen?.(canvas);
        }}
      >
        <CanvasPreview canvas={canvas} index={index} ghost={newTile || placeholder} />
      </button>
      {canManage && (
        <div ref={menuRef} className="project-menu-wrap">
          <button
            className="project-menu-trigger"
            type="button"
            aria-label="项目操作"
            onClick={(event) => {
              event.stopPropagation();
              setMenuOpen((value) => !value);
            }}
          >
            <MoreHorizontal size={18} />
          </button>
          {menuOpen && (
            <div className="project-menu-popover">
              {canvas?.public_project_code && (
                <button
                  type="button"
                  onClick={() => {
                    if (canvas) void onShare?.(canvas);
                    setMenuOpen(false);
                  }}
                >
                  <Copy size={17} />
                  复制编号
                </button>
              )}
              {canMutate && (
                <button
                  type="button"
                  onClick={() => {
                    startRename();
                    setMenuOpen(false);
                  }}
                >
                  <Pencil size={17} />
                  重命名
                </button>
              )}
              {canMutate && (
                <button
                  className="danger"
                  type="button"
                  onClick={() => {
                    if (canvas) onDelete?.(canvas);
                    setMenuOpen(false);
                  }}
                >
                  <Trash2 size={17} />
                  删除
                </button>
              )}
            </div>
          )}
        </div>
      )}
      <div className={`project-title-block ${editing ? 'editing' : ''}`}>
        {editing ? (
          <input
            ref={titleInputRef}
            className="project-title-input"
            value={draftTitle}
            disabled={savingTitle}
            onChange={(event) => setDraftTitle(event.target.value)}
            onBlur={() => void commitRename()}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.currentTarget.blur();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                setEditing(false);
                setDraftTitle(canvas?.title || 'Untitled');
              }
            }}
          />
        ) : (
          <button
            className="project-title-button"
            type="button"
            onClick={() => {
              if (newTile) onCreate?.();
              else if (canvas) onOpen?.(canvas);
            }}
          >
            <strong>{title}</strong>
          </button>
        )}
        {date && <span className="project-updated-text">{date}</span>}
      </div>
    </article>
  );
}

function SkillsPanel({
  skills,
  categories,
  category,
  onCategoryChange,
  onSelectSkill,
}: {
  skills: VoxelSkill[];
  categories: string[];
  category: string;
  onCategoryChange: (category: string) => void;
  onSelectSkill: (skill: VoxelSkill) => void;
}) {
  const visibleSkills = skills.filter((skill) => skill.category === category);

  return (
    <div className="skills-panel" role="dialog" aria-label="技能">
      <header className="skills-panel-head">
        <strong>技能</strong>
        <span>{skills.length} 个开源技能</span>
      </header>
      <div className="skill-tabs" role="tablist" aria-label="技能分类">
        {categories.map((item) => (
          <button
            key={item}
            className={item === category ? 'active' : ''}
            type="button"
            role="tab"
            aria-selected={item === category}
            onClick={() => onCategoryChange(item)}
          >
            {localizedSkillCategory(item)}
          </button>
        ))}
      </div>
      <div className="skill-list">
        {visibleSkills.map((skill) => {
          const Icon = skillIcon(skill);
          const copy = localizedSkill(skill);
          return (
            <button
              key={skill.id}
              className="skill-row"
              type="button"
              title={`${copy.description}${skill.local_path ? `\n${skill.local_path}` : ''}`}
              onClick={() => onSelectSkill(skill)}
            >
              <span className="skill-icon"><Icon size={16} /></span>
              <span className="skill-copy">
                <strong>{copy.title}</strong>
                <em>{copy.description}</em>
              </span>
              <span className="skill-status">{skill.license || 'OSS'}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function HomeComposer({ onCreate, creating }: { onCreate: CreateCanvas; creating: boolean }) {
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [skillsMenuOpen, setSkillsMenuOpen] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<VoxelSkill | null>(null);
  const [promptValue, setPromptValue] = useState('');
  const [smartCanvasEnabled, setSmartCanvasEnabled] = useState(false);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const skillsRegistry = useSkills();
  const skillCategories = skillsRegistry.categories?.length ? skillsRegistry.categories : fallbackSkillCategories;
  const skills = skillsRegistry.skills?.length ? skillsRegistry.skills : fallbackSkills;
  const [skillCategory, setSkillCategory] = useState(skillCategories[0] || 'Video');
  const hasPromptContent = promptValue.trim().length > 0;
  const plusMenuRef = useRef<HTMLDivElement | null>(null);
  const skillsMenuRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!plusMenuOpen) return undefined;
    function close(event: PointerEvent) {
      if (!plusMenuRef.current?.contains(event.target as Node)) setPlusMenuOpen(false);
    }
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [plusMenuOpen]);

  useEffect(() => {
    if (!skillsMenuOpen) return undefined;
    function close(event: PointerEvent) {
      if (!skillsMenuRef.current?.contains(event.target as Node)) setSkillsMenuOpen(false);
    }
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [skillsMenuOpen]);

  useEffect(() => {
    if (!skillCategories.includes(skillCategory)) setSkillCategory(skillCategories[0] || 'Video');
  }, [skillCategories, skillCategory]);

  function executeSkill(skill: VoxelSkill) {
    const copy = localizedSkill(skill);
    setSelectedSkill(skill);
    setPromptValue(copy.prompt || `使用${copy.title}技能，在无限画布上创建一个设计工作流。`);
    setSkillsMenuOpen(false);
    onCreate({ title: `${copy.title}技能`, icon: '✦', kind: 'classic' });
  }

  function submitPrompt() {
    const selectedCopy = selectedSkill ? localizedSkill(selectedSkill) : null;
    const canvasKind: CanvasKind = smartCanvasEnabled ? 'smart' : 'classic';
    const fallbackTitle = smartCanvasEnabled ? '新建智能画布' : '新建画布';
    onCreate({
      title: selectedCopy ? `${selectedCopy.title}技能` : promptValue.trim().slice(0, 52) || fallbackTitle,
      icon: selectedSkill ? '✦' : smartCanvasEnabled ? 'sparkles' : '∞',
      kind: canvasKind,
    });
  }

  return (
    <section className="home-center">
      <div className="home-title">
        <LogoMark />
        <h1>Voxel 让设计更简单</h1>
      </div>
      <p>懂你的设计代理，帮你搞定一切</p>
      <div className="prompt-box">
        <textarea
          placeholder="让 Voxel 打造引人注目的社交媒体视觉"
          value={promptValue}
          onChange={(event) => setPromptValue(event.target.value)}
        />
        <div className="prompt-tools">
          <div>
            <div ref={plusMenuRef} className="prompt-plus-wrap">
              <button
                className={plusMenuOpen ? 'active' : ''}
                type="button"
                aria-label="添加"
                onClick={() => setPlusMenuOpen((value) => !value)}
              >
                <Plus size={20} />
              </button>
              {plusMenuOpen && (
                <div className="prompt-plus-menu">
                  <button
                    type="button"
                    onClick={() => {
                      fileInputRef.current?.click();
                      setPlusMenuOpen(false);
                    }}
                  >
                    <Upload size={16} />
                    上传文件
                  </button>
                  <button className="web-search-row" type="button" onClick={() => setWebSearchEnabled((value) => !value)}>
                    <Globe2 size={16} />
                    网页搜索
                    <span className={`toggle-switch ${webSearchEnabled ? 'on' : ''}`}>
                      <i />
                    </span>
                  </button>
                </div>
              )}
              <input ref={fileInputRef} className="prompt-file-input" type="file" multiple />
            </div>
            <div ref={skillsMenuRef} className="prompt-skills-wrap">
              <button
                className={skillsMenuOpen ? 'active' : ''}
                type="button"
                aria-label="打开技能"
                onClick={() => {
                  setSkillsMenuOpen((value) => !value);
                  setPlusMenuOpen(false);
                }}
              >
                <BookOpen size={19} />
              </button>
              {skillsMenuOpen && (
                <SkillsPanel
                  skills={skills}
                  categories={skillCategories}
                  category={skillCategory}
                  onCategoryChange={setSkillCategory}
                  onSelectSkill={executeSkill}
                />
              )}
            </div>
          </div>
          <div>
            <button
              className={`prompt-smart-toggle ${smartCanvasEnabled ? 'active' : ''}`}
              type="button"
              role="switch"
              aria-checked={smartCanvasEnabled}
              aria-label={smartCanvasEnabled ? '智能编排模式' : '无限画布模式'}
              title={smartCanvasEnabled ? '智能编排' : '无限画布'}
              onClick={() => setSmartCanvasEnabled((value) => !value)}
            >
              <span className="smart-toggle-track">
                <span className="smart-toggle-label">{smartCanvasEnabled ? '智能编排' : '无限画布'}</span>
                <span className="smart-toggle-thumb">
                  <Lightbulb size={15} />
                </span>
              </span>
            </button>
            <button className={`send ${hasPromptContent ? 'active' : ''}`} type="button" onClick={submitPrompt} disabled={creating} aria-label="发送并创建">
              <ArrowUp size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeView({
  auth,
  canvases,
  loading,
  error,
  onCreate,
  creating,
  onOpen,
  onRename,
  onDelete,
  onShare,
  onLogout,
}: {
  auth: AuthState;
  canvases: CanvasRecord[];
  loading: boolean;
  error: string;
  onCreate: CreateCanvas;
  creating: boolean;
  onOpen: (canvas: CanvasRecord) => void;
  onRename: RenameCanvas;
  onDelete: (canvas: CanvasRecord) => void;
  onShare: ShareCanvas;
  onLogout: () => void;
}) {
  const recent = canvases.slice(0, 4);
  const homeInspirationItems = Array.from({ length: 3 }).flatMap((_, groupIndex) =>
    inspirationItems.map((item) => ({
      ...item,
      id: `${item.id}-home-${groupIndex}`,
      height: groupIndex === 0 ? item.height : Math.max(230, item.height + ((groupIndex % 2 ? 34 : -22))),
    })),
  );

  return (
    <main className="lovart-page home-page">
      <CreditsBar auth={auth} onLogout={onLogout} />
      <HomeComposer onCreate={onCreate} creating={creating} />
      <section className="recent-section">
        <div className="section-line">
          <h2>最近项目</h2>
          <button type="button" onClick={() => navigate('/app/projects')}>查看全部 <ChevronRight size={14} /></button>
        </div>
        {error && <div className="inline-error">{error}</div>}
        <div className="project-wall home-wall">
          <ProjectTile index={0} newTile onCreate={onCreate} />
          {loading
            ? Array.from({ length: 4 }).map((_, index) => <ProjectTile key={index} index={index + 1} placeholder />)
            : recent.map((canvas, index) => (
              <ProjectTile
                key={canvas.id}
                canvas={canvas}
                index={index + 1}
                onOpen={onOpen}
                onRename={onRename}
                onDelete={onDelete}
                onShare={onShare}
              />
            ))}
          {!loading &&
            Array.from({ length: Math.max(0, 4 - recent.length) }).map((_, index) => (
              <ProjectTile key={`placeholder-${index}`} index={index + recent.length + 1} placeholder />
            ))}
        </div>
      </section>
      <section className="home-inspiration-section">
        <div className="section-line">
          <h2>灵感发现</h2>
          <button type="button" onClick={() => navigate('/app/templates')}>查看全部 <ChevronRight size={14} /></button>
        </div>
        <div className="inspiration-masonry home-inspiration-masonry">
          {homeInspirationItems.map((item, index) => (
            <article key={item.id} className="inspiration-card">
              <button type="button" onClick={() => onCreate()}>
                <InspirationVisual item={item} />
              </button>
              <div className="inspiration-meta">
                <span className="author-dot" style={{ background: item.tone }} />
                <strong>{item.author}</strong>
                <em><PlaySquare size={13} /> {item.views + index * 37}</em>
                <em><Star size={13} /> {item.likes + (index % 9)}</em>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function ProjectCodeOpener({ onOpenCode }: { onOpenCode: OpenProjectCode }) {
  const [code, setCode] = useState('');
  const [opening, setOpening] = useState(false);
  const [message, setMessage] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = normalizeProjectCode(code);
    if (!isPublicProjectCode(normalized)) {
      setMessage('请输入有效的项目编号');
      return;
    }
    setOpening(true);
    setMessage('');
    try {
      await onOpenCode(normalized);
      setCode('');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : '项目编号无法打开');
    } finally {
      setOpening(false);
    }
  }

  return (
    <form className="project-code-opener" onSubmit={submit}>
      <div>
        <Share2 size={15} />
        <input
          value={code}
          placeholder="输入项目编号"
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setCode(event.target.value)}
        />
      </div>
      <button type="submit" disabled={opening || !code.trim()}>
        {opening ? '打开中' : '打开'}
      </button>
      {message && <span>{message}</span>}
    </form>
  );
}

function ProjectsView({
  auth,
  canvases,
  loading,
  error,
  onCreate,
  onOpen,
  onRename,
  onDelete,
  onShare,
  onOpenCode,
  onLogout,
}: {
  auth: AuthState;
  canvases: CanvasRecord[];
  loading: boolean;
  error: string;
  onCreate: CreateCanvas;
  onOpen: (canvas: CanvasRecord) => void;
  onRename: RenameCanvas;
  onDelete: (canvas: CanvasRecord) => void;
  onShare: ShareCanvas;
  onOpenCode: OpenProjectCode;
  onLogout: () => void;
}) {
  const slots = useMemo(() => {
    if (loading) return Array.from({ length: 14 }).map((_, index) => ({ index, placeholder: true }));
    return canvases.map((canvas, index) => ({ index, canvas }));
  }, [canvases, loading]);

  return (
    <main className="lovart-page project-page">
      <CreditsBar auth={auth} onLogout={onLogout} />
      <section className="content-wide">
        <div className="page-title-row">
          <h1>项目</h1>
          <div className="page-title-actions">
            <ProjectCodeOpener onOpenCode={onOpenCode} />
            <button type="button" aria-label="筛选"><Filter size={20} /></button>
          </div>
        </div>
        {error && <div className="inline-error">{error}</div>}
        <div className="project-wall management-wall">
          <ProjectTile index={0} newTile onCreate={onCreate} />
          {slots.map((item) =>
            'canvas' in item ? (
              <ProjectTile
                key={item.canvas.id}
                canvas={item.canvas}
                index={item.index + 1}
                onOpen={onOpen}
                onRename={onRename}
                onDelete={onDelete}
                onShare={onShare}
              />
            ) : (
              <ProjectTile key={`slot-${item.index}`} index={item.index + 1} placeholder />
            ),
          )}
        </div>
      </section>
    </main>
  );
}

function InspirationVisual({ item }: { item: InspirationItem }) {
  return (
    <div className="inspiration-visual" style={{ height: item.height, '--tone': item.tone } as CSSProperties}>
      {item.image ? (
        <img src={item.image} alt={item.title} />
      ) : (
        <div className={`generated-visual visual-${item.id}`}>
          <span />
          <span />
          <span />
          <strong>{item.title}</strong>
        </div>
      )}
    </div>
  );
}

function TemplatesView({ auth, onCreate, onLogout }: { auth: AuthState; onCreate: CreateCanvas; onLogout: () => void }) {
  const [category, setCategory] = useState('全部');
  const visibleItems = category === '全部' ? inspirationItems : inspirationItems.filter((item) => item.category === category);

  return (
    <main className="lovart-page inspiration-page">
      <CreditsBar auth={auth} onLogout={onLogout} />
      <section className="content-wide">
        <h1 className="inspiration-title">灵感发现</h1>
        <div className="category-tabs">
          {templateCategories.map((item) => (
            <button key={item} className={item === category ? 'active' : ''} type="button" onClick={() => setCategory(item)}>
              {item}
            </button>
          ))}
        </div>
        <div className="inspiration-masonry">
          {visibleItems.map((item) => (
            <article key={item.id} className="inspiration-card">
              <button type="button" onClick={() => onCreate()}>
                <InspirationVisual item={item} />
              </button>
              <div className="inspiration-meta">
                <span className="author-dot" style={{ background: item.tone }} />
                <strong>{item.author}</strong>
                <em><PlaySquare size={13} /> {item.views}</em>
                <em><Star size={13} /> {item.likes}</em>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}

function CanvasEditorView({
  auth,
  canvas,
  canvasId,
  onCreate,
  onDelete,
  onShare,
  onLogout,
}: {
  auth: AuthState;
  canvas?: CanvasRecord;
  canvasId: string;
  onCreate: CreateCanvas;
  onDelete: DeleteCanvas;
  onShare: ShareCanvas;
  onLogout: () => void;
}) {
  const isSmartCanvas = (canvas?.kind || 'classic') === 'smart';
  const canvasMode = isSmartCanvas ? 'smart' : 'classic';
  const readonly = Boolean(canvas?.readonly);
  const iframeSrc = `/static/canvas.html?id=${encodeURIComponent(canvasId)}&embed=1&shell=lovart&mode=${canvasMode}&readonly=${readonly ? '1' : '0'}&v=voxel`;
  const title = canvas?.title || 'Untitled';
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    if (!projectMenuOpen) return undefined;
    function close(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setProjectMenuOpen(false);
    }
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [projectMenuOpen]);

  function sendCanvasCommand(command: string, payload: Record<string, unknown> = {}) {
    iframeRef.current?.contentWindow?.postMessage({ source: 'voxel-shell', command, ...payload }, window.location.origin);
  }

  async function deleteCurrentProject() {
    if (!canvas) return;
    const deleted = await onDelete(canvas);
    if (deleted) navigate('/app/projects');
  }

  function importImages(files: FileList | null) {
    if (readonly) return;
    const selectedFiles = files ? Array.from(files) : [];
    if (!selectedFiles.length) return;
    sendCanvasCommand('importFiles', { files: selectedFiles });
    if (importInputRef.current) importInputRef.current.value = '';
    setProjectMenuOpen(false);
  }

  return (
    <main className="canvas-studio">
      <section className="canvas-main">
        <header className="canvas-topline">
          <div ref={menuRef} className="canvas-project-menu-wrap">
            <button
              type="button"
              className="canvas-project-trigger"
              aria-label="项目菜单"
              onClick={() => setProjectMenuOpen((value) => !value)}
            >
              {projectMenuOpen ? <Menu className="canvas-menu-bars" size={18} /> : <LogoMark />}
              <strong>{title}</strong>
              {readonly && <span className="readonly-badge">只读</span>}
              <Palette size={16} />
              <ChevronDown size={14} />
            </button>
            {projectMenuOpen && (
              <div className="canvas-project-menu">
                <button type="button" onClick={() => navigate('/app/')}>
                  <span>主页</span>
                </button>
                <button type="button" onClick={() => navigate('/app/projects')}>
                  <span>项目库</span>
                </button>
                <div className="canvas-menu-separator" />
                {canvas?.public_project_code && (
                  <button
                    type="button"
                    onClick={() => {
                      void onShare(canvas);
                      setProjectMenuOpen(false);
                    }}
                  >
                    <span>复制项目编号</span>
                    <kbd>{canvas.public_project_code}</kbd>
                  </button>
                )}
                {!readonly && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        setProjectMenuOpen(false);
                        onCreate();
                      }}
                    >
                      <span>新建项目</span>
                    </button>
                    <button className="danger" type="button" onClick={deleteCurrentProject} disabled={!canvas}>
                      <span>删除当前项目</span>
                    </button>
                    <div className="canvas-menu-separator" />
                    <button type="button" onClick={() => importInputRef.current?.click()}>
                      <span>导入图片</span>
                    </button>
                    <div className="canvas-menu-separator" />
                    <button className="muted" type="button" onClick={() => sendCanvasCommand('undo')}>
                      <span>撤销</span>
                      <kbd>⌘ Z</kbd>
                    </button>
                  </>
                )}
                <button className="muted" type="button" disabled>
                  <span>重做</span>
                  <kbd>⌘ ⇧ Z</kbd>
                </button>
                {!readonly && (
                  <button className="muted" type="button" onClick={() => sendCanvasCommand('copy')}>
                    <span>复制对象</span>
                    <kbd>⌘ D</kbd>
                  </button>
                )}
                <button type="button" onClick={() => sendCanvasCommand('fit')}>
                  <span>显示画布所有元素</span>
                  <kbd>⇧ 1</kbd>
                </button>
                <button type="button" onClick={() => sendCanvasCommand('zoomIn')}>
                  <span>放大</span>
                  <kbd>⌘ +</kbd>
                </button>
                <button type="button" onClick={() => sendCanvasCommand('zoomOut')}>
                  <span>缩小</span>
                  <kbd>⌘ -</kbd>
                </button>
              </div>
            )}
            <input
              ref={importInputRef}
              className="canvas-import-input"
              type="file"
              accept="image/*"
              multiple
              onChange={(event) => importImages(event.target.files)}
            />
          </div>
          <span className="cloud-pill"><Layers3 size={16} /></span>
        </header>
        <CreditsBar auth={auth} onLogout={onLogout} />
        <iframe ref={iframeRef} className="canvas-iframe" src={iframeSrc} title={title} />
      </section>
      <aside className="chat-panel">
        <header>
          <h2>新对话</h2>
          <div>
            <button type="button" aria-label="添加"><MessageSquare size={15} /></button>
            <button type="button" aria-label="展开"><ChevronDown size={15} /></button>
            <button type="button" aria-label="分享"><Share2 size={15} /></button>
            <button type="button" aria-label="收起"><ArrowUp size={15} /></button>
          </div>
        </header>
        <div className="skill-suggestions">
          <strong>试试这些 Voxel 技能</strong>
          <div>
            <button type="button"><Video size={14} /> Seedance 2.0 视频创作</button>
            <button type="button"><PlaySquare size={14} /> 一键到底视频</button>
            <button type="button"><Image size={14} /> Instagram Post</button>
            <button type="button"><Sparkles size={14} /> 一键跨平台适配</button>
            <button type="button"><Box size={14} /> Logo 设计</button>
            <button type="button"><Palette size={14} /> UGC：生活化上身图</button>
            <button type="button"><WandSparkles size={14} /> AI 造型师：高转化模特图</button>
            <button type="button"><BookOpen size={14} /> 所有技能</button>
          </div>
        </div>
        <div className="chat-input">
          <textarea placeholder='从一个想法开始，或输入 "@" 引用内容' />
          <div>
            <button className="icon-button" type="button" aria-label="添加"><Plus size={19} /></button>
            <button className="icon-button" type="button" aria-label="技能"><BookOpen size={18} /></button>
            <button type="button"><LogoMark small /> 智能体 <ChevronDown size={13} /></button>
            <span />
            <button className="icon-button" type="button" aria-label="提示"><Lightbulb size={17} /></button>
            <button className="icon-button" type="button" aria-label="资源"><Box size={17} /></button>
            <button className="icon-button voice" type="button" aria-label="语音"><Settings2 size={17} /></button>
          </div>
        </div>
      </aside>
    </main>
  );
}

function OriginalReferenceView() {
  return (
    <main className="original-reference-page">
      <header className="original-reference-bar">
        <div>
          <strong>GitHub 原始 UI 参考</strong>
          <span>用于功能调试、按钮流程和行为对照，不参与新版平台改造。</span>
        </div>
        <a href="/static/index.html" target="_blank" rel="noreferrer">
          打开原始页面
        </a>
      </header>
      <iframe
        className="original-reference-frame"
        src="/static/index.html?voxel_reference=1"
        title="GitHub 原始 Infinite Canvas 页面"
      />
    </main>
  );
}

export function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute());
  const [creating, setCreating] = useState(false);
  const [routeCanvas, setRouteCanvas] = useState<CanvasRecord | null>(null);
  const { auth, applyAuth, logout } = useAuth();
  const { canvases, loading, error, refresh, setCanvases } = useCanvases(auth.authenticated);

  useEffect(() => {
    function syncRoute() {
      setRoute(parseRoute());
    }
    window.addEventListener('popstate', syncRoute);
    return () => window.removeEventListener('popstate', syncRoute);
  }, []);

  const openCanvas = useCallback((canvas: CanvasRecord) => {
    navigate(routePath({ name: 'canvas', canvasId: canvas.id }));
  }, []);

  useEffect(() => {
    if (route.name !== 'canvas') {
      setRouteCanvas(null);
      return;
    }
    if (!auth.authenticated) return;
    if (canvases.some((item) => item.id === route.canvasId)) {
      setRouteCanvas(null);
      return;
    }
    let cancelled = false;
    apiJson<CanvasRecord>(`/api/canvases/${encodeURIComponent(route.canvasId)}/meta`, { cache: 'no-store' })
      .then((canvas) => {
        if (!cancelled) setRouteCanvas(canvas);
      })
      .catch((err) => {
        if (!cancelled) {
          setRouteCanvas({
            id: route.canvasId,
            title: err instanceof Error ? err.message : '项目不存在',
            readonly: true,
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [auth.authenticated, canvases, route]);

  const createCanvas = useCallback(async (options: CreateCanvasOptions = {}) => {
    setCreating(true);
    const loaderStartedAt = Date.now();
    try {
      const data = await apiJson<{ canvas: CanvasRecord }>('/api/canvases', {
        method: 'POST',
        body: JSON.stringify({
          title: options.title || (options.kind === 'smart' ? '新建智能画布' : '新建画布'),
          icon: options.icon || (options.kind === 'smart' ? 'sparkles' : '∞'),
          kind: options.kind || 'classic',
        }),
      });
      setCanvases((items) => [data.canvas, ...items.filter((item) => item.id !== data.canvas.id)]);
      navigate(routePath({ name: 'canvas', canvasId: data.canvas.id }));
      refresh();
    } finally {
      const elapsed = Date.now() - loaderStartedAt;
      if (elapsed < CANVAS_CREATE_MIN_LOADER_MS) {
        await new Promise((resolve) => window.setTimeout(resolve, CANVAS_CREATE_MIN_LOADER_MS - elapsed));
      }
      setCreating(false);
    }
  }, [refresh, setCanvases]);

  const renameCanvas = useCallback(
    async (canvas: CanvasRecord, title: string) => {
      try {
        const data = await apiJson<{ canvas: CanvasRecord }>(`/api/canvases/${canvas.id}/meta`, {
          method: 'POST',
          body: JSON.stringify({ title }),
        });
        setCanvases((items) => items.map((item) => (item.id === canvas.id ? data.canvas : item)));
        return true;
      } catch (err) {
        window.alert(err instanceof Error ? err.message : '重命名失败');
        return false;
      }
    },
    [setCanvases],
  );

  const deleteCanvas = useCallback(
    async (canvas: CanvasRecord) => {
      if (canvas.readonly) {
        window.alert('只读项目不能删除');
        return false;
      }
      const title = canvas.title || 'Untitled';
      if (!window.confirm(`删除项目「${title}」？删除后可在原始画布回收站中恢复。`)) return false;

      try {
        await apiJson<{ ok: boolean }>(`/api/canvases/${canvas.id}`, { method: 'DELETE' });
        setCanvases((items) => items.filter((item) => item.id !== canvas.id));
        return true;
      } catch (err) {
        window.alert(err instanceof Error ? err.message : '删除失败');
        return false;
      }
    },
    [setCanvases],
  );

  const shareCanvas = useCallback(async (canvas: CanvasRecord) => {
    const code = canvas.public_project_code || '';
    if (!code) {
      window.alert('这个项目还没有公开编号');
      return;
    }
    const copied = await copyText(code);
    window.alert(copied ? `已复制项目编号：${code}` : `项目编号：${code}`);
  }, []);

  const openProjectCode = useCallback(async (code: string) => {
    const normalized = normalizeProjectCode(code);
    const data = await apiJson<{ canvas: CanvasRecord }>(`/api/projects/resolve/${encodeURIComponent(normalized)}`, { cache: 'no-store' });
    setRouteCanvas(data.canvas);
    navigate(routePath({ name: 'canvas', canvasId: normalized }));
  }, []);

  const currentCanvas = route.name === 'canvas'
    ? canvases.find((item) => item.id === route.canvasId) || routeCanvas || undefined
    : undefined;

  if (auth.loading) {
    return (
      <main className="auth-page auth-loading">
        <div className="canvas-create-loader">
          <img src={VOXEL_LOADER_SRC} alt="" aria-hidden="true" draggable={false} />
          <span>正在进入 Voxel</span>
        </div>
      </main>
    );
  }

  if (!auth.authenticated) {
    return <AuthView onAuthenticated={applyAuth} nextPath={route.name === 'canvas' ? routePath(route) : '/app/'} />;
  }

  if (route.name === 'canvas') {
    return (
      <>
        <CanvasEditorView
          auth={auth}
          canvas={currentCanvas}
          canvasId={route.canvasId}
          onCreate={createCanvas}
          onDelete={deleteCanvas}
          onShare={shareCanvas}
          onLogout={logout}
        />
        {creating && <CanvasCreationOverlay />}
      </>
    );
  }

  return (
    <div className="platform-shell">
      <SideRail route={route} onCreate={createCanvas} creating={creating} />
      {route.name === 'home' && (
        <HomeView
          canvases={canvases}
          loading={loading}
          error={error}
          auth={auth}
          onCreate={createCanvas}
          creating={creating}
          onOpen={openCanvas}
          onRename={renameCanvas}
          onDelete={deleteCanvas}
          onShare={shareCanvas}
          onLogout={logout}
        />
      )}
      {route.name === 'projects' && (
        <ProjectsView
          canvases={canvases}
          loading={loading}
          error={error}
          auth={auth}
          onCreate={createCanvas}
          onOpen={openCanvas}
          onRename={renameCanvas}
          onDelete={deleteCanvas}
          onShare={shareCanvas}
          onOpenCode={openProjectCode}
          onLogout={logout}
        />
      )}
      {route.name === 'templates' && <TemplatesView auth={auth} onCreate={createCanvas} onLogout={logout} />}
      {route.name === 'original' && <OriginalReferenceView />}
      {creating && <CanvasCreationOverlay />}
    </div>
  );
}
